import { useMemo } from 'react';

import { getTableFilters } from '../filterTypes';
import { sortTableHeaders } from '../sortTableHeaders';
import { filterTableHeaders } from '../filterTableHeaders';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import { useCreateColumns } from './useCreateColumns';

export interface UseTableColumnsProps {
  shipData: any[] | undefined;
}

/**
 * Custom hook for generating table columns and filters
 * Handles column creation and filter generation with density support
 * Note: Actions column should be added separately to avoid circular dependencies
 */
export const useTableColumns = ({ shipData }: UseTableColumnsProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  // Stable signature of the column structure so we only recompute when the set
  // of headers actually changes - not on every websocket data tick (which would
  // remount cells and close any open row-action dropdown).
  const headerSignature =
    shipData && shipData.length > 0 ? Object.keys(shipData[0]).join('|') : '';

  // Process headers
  const processedHeaders = useMemo(() => {
    if (!headerSignature) {
      return [];
    }

    const rawHeaders = headerSignature.split('|');
    const filteredHeaders = filterTableHeaders(rawHeaders);
    return sortTableHeaders(filteredHeaders);
  }, [headerSignature]);

  // Generate filters
  const filters = useMemo(() => {
    if (processedHeaders.length === 0) {
      return [];
    }
    return getTableFilters(processedHeaders);
  }, [processedHeaders]);

  // Use the hook to create density-aware columns
  const columns = useCreateColumns(
    processedHeaders,
    formatDateTimeWithTimeZone,
  );

  /**
   * Get column information for debugging
   */
  const getColumnInfo = useMemo(() => {
    if (!shipData || shipData.length === 0) {
      return {
        totalColumns: 0,
        headerNames: [],
        processedHeaders: [],
      };
    }

    return {
      totalColumns: columns.length,
      headerNames: Object.keys(shipData[0]),
      processedHeaders,
    };
  }, [columns, shipData, processedHeaders]);

  return {
    columns,
    filters,
    processedHeaders,
    getColumnInfo,
  };
};
